import { useEffect, useReducer, useSyncExternalStore } from 'react';

import {
  rateLimitedUntil,
  subscribeRateLimitGuard,
  type RateLimitBudget
} from '@/lib/rate-limit-guard';

/**
 * Whole seconds a deliberate retry has to wait before the rate limit guard
 * for `budget` lets it reach the network; 0 when it may go now.
 *
 * A retry control disables itself and says how long ("Try again in 40 s",
 * `formatRateLimitWait` in `@/lib/format/duration/rate-limit-wait`)
 * while this is above zero, rather than clearing the guard: clicking again
 * into a spent budget only spends it further. Re-renders once a second
 * while counting, and at once when the guard is armed or lifted.
 */
export function useRateLimitWait(budget: RateLimitBudget = 'global'): number {
  const until = useSyncExternalStore(
    subscribeRateLimitGuard,
    () => rateLimitedUntil(budget),
    () => 0
  );
  const [, tick] = useReducer((count: number) => count + 1, 0);
  const seconds = Math.ceil(Math.max(0, until - Date.now()) / 1000);

  useEffect(() => {
    if (seconds <= 0) return;
    // To the next whole second, so the label and the window end together.
    const delay = Math.max(0, until - Date.now()) % 1000 || 1000;
    const timer = setTimeout(tick, delay);
    return () => clearTimeout(timer);
  }, [until, seconds]);

  return seconds;
}

/**
 * Whole seconds until `until` (ms since epoch): 0 once it has passed, or
 * when there is none. For a wait an RPC node asked for, which the backend
 * guard above does not track. Re-renders once a second while counting.
 */
export function useSecondsUntil(until: number | null): number {
  const [, tick] = useReducer((count: number) => count + 1, 0);
  const seconds =
    until === null ? 0 : Math.ceil(Math.max(0, until - Date.now()) / 1000);

  useEffect(() => {
    if (until === null || seconds <= 0) return;
    // To the next whole second, so the label and the wait end together.
    const delay = Math.max(0, until - Date.now()) % 1000 || 1000;
    const timer = setTimeout(tick, delay);
    return () => clearTimeout(timer);
  }, [until, seconds]);

  return seconds;
}
